import { t } from "@lingui/macro";
import { RouteProp, useRoute } from "@react-navigation/native";
import { FlashList } from "@shopify/flash-list";
import graphql from "babel-plugin-relay/macro";
import {
  addDays,
  differenceInDays,
  eachDayOfInterval,
  format,
  parse,
  startOfToday,
} from "date-fns";
import { useAtomValue } from "jotai";
import { groupBy } from "lodash";
import {
  Suspense,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  PreloadedQuery,
  useFragment,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { Loader } from "@whoosh/design-system/src/components/display";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { convertDateTimeObjToServerStringFormat } from "@whoosh/utilities/src/dates";

import { LessonsStackNavigatorParamList } from "../../../../../../navigators/LessonsStackNavigator/types";
import { durationAtom, lessonTypeIdAtom } from "../../atoms";
import { InstructorAvailabilityListQuery } from "./__generated__/InstructorAvailabilityListQuery.graphql";
import { InstructorAvailabilityList_facility$key } from "./__generated__/InstructorAvailabilityList_facility.graphql";
import {
  getInitialAvailabilityQueryWindow,
  getNextAvailabilityQueryWindow,
  hasReachedAvailabilityBookingWindow,
  mergeAvailabilitySlots,
} from "./availabilityQueryWindow";
import { AvailabilitySlots } from "./components/AvailabilitySlots";

type Props = {
  facilityRef: InstructorAvailabilityList_facility$key | null;
  setScrolled: (scrolled: boolean) => void;
  openAuthModal: (callback: () => void) => void;
  bookingWindowDays: number;
};

export const InstructorAvailabilityList = ({
  facilityRef,
  setScrolled,
  openAuthModal,
  bookingWindowDays,
}: Props) => {
  const route =
    useRoute<RouteProp<LessonsStackNavigatorParamList, "InstructorDetail">>();
  const instructorId = route.params?.instructorId;

  const dateParam = route.params?.date;

  const facility = useFragment(
    InstructorAvailabilityListFragmentString,
    facilityRef
  );
  const facilityId = facility?.id;

  const isPublic = facility?.club?.supportsPublic ?? false;

  const canSelectVenue = isPublic
    ? facility?.publicLessonsPermissionSet?.patronSelectVenue ?? false
    : facility?.privateLessonsPermissionSet?.patronSelectVenue ?? false;

  const parsedDate = dateParam
    ? parse(dateParam, "yyyy-MM-dd", new Date())
    : undefined;
  const todayDateObj = startOfToday();
  const dayDifference = parsedDate
    ? differenceInDays(parsedDate, todayDateObj)
    : 0;
  const initialQueryWindow = useMemo(
    () => getInitialAvailabilityQueryWindow(dayDifference, bookingWindowDays),
    [bookingWindowDays, dayDifference]
  );

  const durationMins = useAtomValue(durationAtom);
  const [currentAvailabilityQueryWindow, setCurrentAvailabilityQueryWindow] =
    useState(initialQueryWindow);
  const selectedLessonTypeId = useAtomValue(lessonTypeIdAtom);
  const [queryRef, loadQuery] = useQueryLoader<InstructorAvailabilityListQuery>(
    InstructorAvailabilityListQueryString
  );
  const deferredQuery = useDeferredValue(queryRef);
  const deferredWindow = useDeferredValue(currentAvailabilityQueryWindow);

  useEffect(() => {
    setCurrentAvailabilityQueryWindow(initialQueryWindow);
  }, [initialQueryWindow]);

  useEffect(() => {
    if (instructorId && facilityId && durationMins) {
      const endDateTime = convertDateTimeObjToServerStringFormat(
        addDays(new Date(), currentAvailabilityQueryWindow.requestEndDayOffset)
      );
      const nextStartDateTime = convertDateTimeObjToServerStringFormat(
        addDays(
          new Date(),
          currentAvailabilityQueryWindow.requestStartDayOffset
        )
      );

      loadQuery(
        {
          instructorId,
          startDateTime: nextStartDateTime,
          endDateTime,
          durationMins,
          slotAvailability: {
            bookingType: "LESSON",
            facilityId: facilityId,
            instructorIds: [instructorId],
            lessonTypeId: selectedLessonTypeId,
          },
          facilityId,
        },
        { fetchPolicy: "store-and-network" }
      );
    }
  }, [
    currentAvailabilityQueryWindow,
    durationMins,
    facilityId,
    selectedLessonTypeId,
    instructorId,
    loadQuery,
  ]);

  const onLoadMore = useCallback(() => {
    setCurrentAvailabilityQueryWindow((prev) =>
      getNextAvailabilityQueryWindow(prev, bookingWindowDays)
    );
  }, [bookingWindowDays]);

  return (
    <Suspense fallback={<Loader />}>
      {deferredQuery && facilityId ? (
        <AvailabilityList
          initialDate={dateParam}
          instructorId={instructorId}
          facilityId={facilityId}
          windowSize={deferredWindow.loadedEndDayOffset}
          requestStartDayOffset={deferredWindow.requestStartDayOffset}
          openAuthModal={openAuthModal}
          onLoadMore={onLoadMore}
          hasMoreAvailability={
            !hasReachedAvailabilityBookingWindow(
              deferredWindow,
              bookingWindowDays
            )
          }
          queryRef={deferredQuery}
          prevQueryRef={queryRef}
          setScrolled={setScrolled}
          canSelectVenue={canSelectVenue}
        />
      ) : null}
    </Suspense>
  );
};

type InnerProps = {
  queryRef: PreloadedQuery<InstructorAvailabilityListQuery>;
  prevQueryRef:
    | PreloadedQuery<InstructorAvailabilityListQuery>
    | null
    | undefined;
  onLoadMore: () => void;
  hasMoreAvailability: boolean;
  requestStartDayOffset: number;
  windowSize: number;
  facilityId: string;
  instructorId: string;
  initialDate?: string;
  openAuthModal: (callback: () => void) => void;
  setScrolled: (scrolled: boolean) => void;
  canSelectVenue: boolean;
};

const AvailabilityList = ({
  queryRef,
  windowSize,
  facilityId,
  instructorId,
  initialDate,
  requestStartDayOffset,
  openAuthModal,
  prevQueryRef,
  onLoadMore,
  setScrolled,
  hasMoreAvailability,
  canSelectVenue,
}: InnerProps) => {
  const startDate = new Date();
  const endDate = addDays(new Date(), windowSize);

  const dateRange = eachDayOfInterval({ start: startDate, end: endDate });

  const data = usePreloadedQuery(
    InstructorAvailabilityListQueryString,
    queryRef
  );

  const availabilitySlots = useMemo(
    () =>
      data.node?.availabilitySlots?.edges?.flatMap((e) =>
        e?.node ? [e.node] : []
      ) ?? [],
    [data.node?.availabilitySlots?.edges]
  );
  const [allAvailabilitySlots, setAllAvailabilitySlots] =
    useState(availabilitySlots);

  useEffect(() => {
    if (requestStartDayOffset === 0) {
      setAllAvailabilitySlots(availabilitySlots);
      return;
    }

    setAllAvailabilitySlots((existingSlots) =>
      mergeAvailabilitySlots(existingSlots, availabilitySlots)
    );
  }, [availabilitySlots, requestStartDayOffset]);

  const groupedByDate = groupBy(allAvailabilitySlots, "date");

  const dateList = dateRange.reduce((acc, date) => {
    const dateString = format(date, "yyyy-MM-dd");
    const slots = groupedByDate[dateString];

    if (slots) {
      acc.push({ date: format(date, "EEE, LLL do"), slots });
    } else {
      const lastEntry = acc[acc.length - 1];
      if (lastEntry && !lastEntry.slots) {
        lastEntry.endDate = format(date, "EEE, LLL do");
      } else {
        acc.push({
          date: format(date, "EEE, LLL do"),
          endDate: format(date, "EEE, LLL do"),
        });
      }
    }

    return acc;
  }, [] as Array<{ date: string; slots?: typeof allAvailabilitySlots; endDate?: string }>);

  const initialScrollIndex = initialDate
    ? dateList.findIndex(({ slots }) => {
        return slots?.some(({ date }) => date === initialDate);
      })
    : undefined;

  const flashList = useRef<any>(null);

  useEffect(() => {
    if (initialScrollIndex && flashList.current) {
      setTimeout(() => {
        flashList.current.scrollToIndex({
          index: initialScrollIndex,
          viewPosition: 0.4,
          animated: true,
        });
      }, 1000);
    }
  }, [initialScrollIndex]);

  return (
    <FlashList
      ref={flashList}
      data={dateList}
      ItemSeparatorComponent={() => <Box height={40} />}
      showsVerticalScrollIndicator={false}
      onScroll={(e) => setScrolled(e.nativeEvent.contentOffset.y > 0)}
      ListHeaderComponent={() => <Box height={64} />}
      ListFooterComponent={() => (
        <Box
          px={6}
          pt={10}
          pb={32}
          maxWidth={{ base: "auto", lg: 640 }}
          minWidth={{ base: "auto", lg: 640 }}
          marginHorizontal={{ base: 0, lg: "auto" }}
        >
          <Button
            isLoading={prevQueryRef !== queryRef}
            variant="secondary"
            size="small"
            onPress={onLoadMore}
            isDisabled={!hasMoreAvailability}
            accessibilityLabel={t`Load More button`}
          >{t`Load More`}</Button>
        </Box>
      )}
      renderItem={({ item: { date, slots, endDate } }) => {
        return (
          <VStack
            gap={4}
            px={6}
            key={date}
            maxWidth={{ base: "auto", lg: 640 }}
            minWidth={{ base: "auto", lg: 640 }}
            marginHorizontal={{ base: 0, lg: "auto" }}
          >
            <Text variant="mainBaseSemiBold">
              {date}
              {endDate && date !== endDate ? ` - ${endDate}` : null}
            </Text>
            {!slots ? (
              <HStack
                justifyContent="center"
                py={3}
                px={4}
                backgroundColor="surface-dividers"
                borderRadius="2"
              >
                <Text color="text-primary">{t`No Availability`}</Text>
              </HStack>
            ) : (
              <AvailabilitySlots
                slots={slots}
                facilityId={facilityId}
                instructorId={instructorId}
                openAuthModal={openAuthModal}
                sessionRef={data.session ?? null}
                facilityNodeRef={data.facilityNode ?? null}
                canSelectVenue={canSelectVenue}
              />
            )}
          </VStack>
        );
      }}
    />
  );
};

const InstructorAvailabilityListFragmentString = graphql`
  fragment InstructorAvailabilityList_facility on Facility {
    id
    publicLessonsPermissionSet {
      bookingWindowDays
      patronSelectVenue
    }
    privateLessonsPermissionSet {
      bookingWindowDays
      patronSelectVenue
    }
    club {
      supportsPublic
    }
  }
`;

const InstructorAvailabilityListQueryString = graphql`
  query InstructorAvailabilityListQuery(
    $instructorId: ID!
    $startDateTime: Datetime!
    $endDateTime: Datetime!
    $durationMins: Int!
    $slotAvailability: SlotAvailabilityArgs
    $facilityId: ID!
  ) {
    session {
      ...AvailabilitySlots_session
    }
    node(id: $instructorId) {
      ... on StaffProfile {
        availabilitySlots(
          dateTimeLte: $endDateTime
          dateTimeGte: $startDateTime
          durationMins: $durationMins
          slotAvailability: $slotAvailability
        ) {
          edges {
            node {
              id
              date
              time
              durationMins
            }
          }
        }
      }
    }
    facilityNode: node(id: $facilityId) {
      ... on Facility {
        ...AvailabilitySlotsButton_facility
      }
    }
  }
`;
